import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../core/models/base-state/base-state';
import { Medication } from '../../models/medication';
import { SelectedMedication } from '../../models/selected-medication';
import {
  getMedicationPrescriptionsFailure,
  getMedicationPrescriptionsSuccess,
  getPatientMedicationById,
  getPatientMedicationByIdFailure,
  getPatientMedicationByIdSuccess,
  getPatientMedications,
  getPatientMedicationsFailure,
  getPatientMedicationsSuccess,
  getSelectedMedicationsSuccess,
  setSelectedMedications,
} from '../actions/medications.actions';

export const medicationFeatureKey = 'medication';

export interface MedicationState extends EntityState<Medication> {
  baseState: baseState.BaseState;
  selectedMedications: SelectedMedication[] | null;
  /**
   * Failures from the single-medication detail read, kept apart from `baseState.error` so a failed
   * detail cannot make the medications list render its error state. See PBI 54129.
   */
  detailError: unknown;
}

export const medicationAdapter = createEntityAdapter<Medication>({
  selectId: medication => medication.patientMedicationId,
});

export const initialState: MedicationState = medicationAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  selectedMedications: null,
  detailError: null,
});

export const medicationReducer = createReducer<MedicationState>(
  initialState,

  on(getPatientMedications, (state): MedicationState => {
    return {
      ...medicationAdapter.removeAll(state),
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(getPatientMedicationsSuccess, (state, { medications }): MedicationState => {
    if (medications !== null) return medicationAdapter.addMany(medications, state);
    return state;
  }),

  on(getPatientMedicationsSuccess, (state): MedicationState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    };
  }),

  on(getPatientMedicationsFailure, (state, { error }): MedicationState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  on(getPatientMedicationById, (state): MedicationState => {
    return {
      ...state,
      detailError: null,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(getPatientMedicationByIdSuccess, (state, { selectedMeds }): MedicationState => {
    return {
      ...state,
      detailError: null,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
      selectedMedications: selectedMeds,
    };
  }),

  // Writes detailError, NOT baseState.error: they shared one slot until PBI 54129, so a failed
  // medication detail read made the medications LIST render "Could not load your medications" once the
  // patient returned to it from the error page.
  on(getPatientMedicationByIdFailure, (state, { error }): MedicationState => {
    return {
      ...state,
      detailError: error,
      baseState: {
        ...state.baseState,
        isLoading: false,
      },
    };
  }),

  on(getSelectedMedicationsSuccess, (state, { medications }): MedicationState => {
    return {
      ...state,
      selectedMedications: medications,
    };
  }),

  on(getMedicationPrescriptionsSuccess, (state, { medicationId, isHomeMedication, prescriptions }): MedicationState => {
    const medication = state.entities[medicationId];

    if (!medication || medication.isHomeMedication !== isHomeMedication) {
      return state;
    }

    return {
      ...state,
      detailError: null,
      entities: {
        ...state.entities,
        [medicationId]: {
          ...medication,
          prescriptions: prescriptions,
        },
      },
    };
  }),

  on(getMedicationPrescriptionsFailure, (state, { error }): MedicationState => {
    return {
      ...state,
      detailError: error,
      baseState: {
        ...state.baseState,
        isLoading: false,
      },
    };
  }),

  on(
    setSelectedMedications,
    (state, { medications }): MedicationState => ({
      ...state,
      selectedMedications: medications,
    })
  )
);
